import React, { useEffect, useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import parse, { Element } from 'html-react-parser';
import { WysiwygComponent } from 'types';
import SlicePadding from 'components/Energy/Layout/SlicePadding';
import './wysiwyg.css';

const Wysiwyg = (data: any) => {
  const {
    content: { text },
    topspacing,
    bottomspacing,
    width,
    align,
    media,
  } = data;

  const classeNames = ['wysiwyg', width, align];

  // One static query per file: combine media document aliases and all File nodes
  const staticData = useStaticQuery(graphql`
    query wysiwygMediaDocAliasesAndWysiwygAllFilesForInlineMedia {
      allMediaDocument {
        nodes {
          drupal_id
          path {
            alias
          }
        }
      }
      allFile {
        nodes {
          id
          base
          publicURL
          extension
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP])
          }
        }
      }
    }
  `);

  const filesByBasename: Record<string, any> = useMemo(() => {
    const map: Record<string, any> = {};
    const nodes = staticData?.allFile?.nodes || [];
    nodes.forEach((f: any) => {
      if (f?.base) map[f.base] = f;
    });
    return map;
  }, [staticData]);

  const docAliasByUuid: Record<string, string> = useMemo(() => {
    const map: Record<string, string> = {};
    const nodes = staticData?.allMediaDocument?.nodes || [];
    nodes.forEach((n: any) => {
      const uuid = n?.drupal_id;
      const alias = n?.path?.alias;
      if (uuid && alias) {
        map[uuid] = alias.startsWith('/') ? alias : `/${alias}`;
      }
    });
    return map;
  }, [staticData]);

  useEffect(() => {
    const documentLinks = document.querySelectorAll('.wysiwyg-wrapper a');

    if (documentLinks.length > 0) {
      documentLinks.forEach(link => {
        const elem = link.getAttribute('href');
        if (elem && elem.startsWith('/document')) {
          link.setAttribute('target', '_blank');
        }
      });
    }

    console.log(documentLinks);
  }, []);

  // Return the markup from the wysiwyg editor.
  return (
    <SlicePadding top={topspacing} bottom={bottomspacing}>
      <div className="wysiwyg-wrapper">
        <div className={classeNames.join(' ')}>
          {parse(text || '', {
            replace: (node: any) => {
              if (node && node.type === 'tag' && node.name === 'a') {
                const el = node as Element;
                const attrs = el.attribs || {};
                const entityType = attrs['data-entity-type'];
                const substitution = attrs['data-entity-substitution'];
                const uuid = attrs['data-entity-uuid'];
                const href = attrs['href'];
                if (entityType === 'media' && substitution === 'canonical' && uuid) {
                  const alias = docAliasByUuid[uuid];
                  if (alias && href !== alias) {
                    el.attribs['href'] = alias;
                    if (alias.startsWith('/document')) {
                      el.attribs['target'] = '_blank';
                    }
                  }
                }
              }
              if (node && node.type === 'tag' && node.name === 'img') {
                const el = node as Element;
                const attrs = el.attribs || {};
                const uuid = attrs['data-entity-uuid'];
                const src: string | undefined = attrs['src'];
                const altAttr: string | undefined = attrs['alt'];

                let m: any | undefined;
                if (uuid && media?.byUuid) {
                  m = media.byUuid[uuid];
                }

                if (!m && src && media?.byBasename) {
                  try {
                    const base = src.split('/').pop();
                    if (base) {
                      m = media.byBasename[base];
                    }
                  } catch (_) {
                    // noop
                  }
                }

                let imageNode = m?.relationships?.field_media_image?.localFile;
                let gImg = imageNode ? getImage(imageNode) : undefined;
                const alt = m?.field_media_image?.alt || altAttr || '';

                // Fallback to any downloaded File node by basename
                if (!gImg && src) {
                  try {
                    const base = src.split('/').pop();
                    if (base && filesByBasename[base]?.childImageSharp) {
                      gImg = getImage(filesByBasename[base]);
                    }
                  } catch (_) {}
                }

                if (gImg) {
                  return <GatsbyImage image={gImg} alt={alt} />;
                }
                // Fallback: keep original <img>
                return undefined;
              }
              // Handle <video> and <source> tags
              if (node && node.type === 'tag' && (node.name === 'video' || node.name === 'source')) {
                const el = node as Element;
                const attrs = el.attribs || {};
                const uuid = attrs['data-entity-uuid'];
                const src: string | undefined = attrs['src'];

                let m: any | undefined;
                if (uuid && media?.byUuid) {
                  m = media.byUuid[uuid];
                }

                if (!m && src && media?.byBasename) {
                  try {
                    const base = src.split('/').pop();
                    if (base) {
                      m = media.byBasename[base];
                    }
                  } catch (_) {
                    // noop
                  }
                }

                let videoUrl: string | undefined = m?.relationships?.field_media_video_file?.localFile?.publicURL;
                let posterUrl: string | undefined = m?.relationships?.thumbnail?.localFile?.publicURL;

                // Fallback by basename for videos/posters
                if (!videoUrl && src) {
                  try {
                    const base = src.split('/').pop();
                    if (base && filesByBasename[base]?.publicURL) {
                      videoUrl = filesByBasename[base].publicURL;
                    }
                  } catch (_) {}
                }

                if (node.name === 'video') {
                  // Render a video with a single MP4 source if available; preserve attributes like controls/loop/muted if present
                  const controls = attrs['controls'] !== undefined || true;
                  const autoPlay = attrs['autoplay'] !== undefined;
                  const loop = attrs['loop'] !== undefined;
                  const muted = attrs['muted'] !== undefined;
                  const playsInline = attrs['playsinline'] !== undefined;
                  const poster = attrs['poster'] || posterUrl;

                  if (videoUrl) {
                    return (
                      <video
                        controls={controls}
                        autoPlay={autoPlay}
                        loop={loop}
                        muted={muted}
                        playsInline={playsInline}
                        poster={poster}
                        style={{ maxWidth: '100%', height: 'auto' }}
                      >
                        <source src={videoUrl} />
                        Your browser does not support the video tag.
                      </video>
                    );
                  }
                  return undefined;
                }

                if (node.name === 'source') {
                  if (videoUrl) {
                    return <source src={videoUrl} />;
                  }
                  return undefined;
                }
              }
              return undefined;
            },
          })}
        </div>
      </div>
    </SlicePadding>
  );
};

export default Wysiwyg;
